import sizeComparison from "assets/icons/psp/illustration-multiple-sites.svg";
import HeaderBackgroundDesktop from "assets/images/psp/hero/dirt-bike-hero.webp";
import HeaderBackgroundMobile from "assets/images/psp/hero/dirt-bike-header-mobile.webp";
import HeaderBackgroundTablet from "assets/images/psp/hero/dirt-bike-header-tablet.webp";
import Industry from "assets/images/psp/relatedContentImages/battery-tender.webp";
import DirtBikeImage from "assets/images/psp/richconentImages/dirt-bike-storage-steps.webp";
import ctaMan from "assets/images/psp/inlineCTA.jpg";
import GeorgeRansom from "assets/images/psp/review-george.png";
import AnnAlarcon from "assets/images/psp/review-ann.png";
import SMurphy from "assets/images/psp/review-murphy.png";
import HowToStore from "assets/images/psp/relatedContentImages/dirt-bike-storage (1).webp";
import DriveUpStorage from "assets/images/psp/relatedContentImages/outdoorparking.webp";
import illustrationSearch from "assets/images/psp/illustration-search.svg";
import { storageCategories } from "../utils/getPSPLinks";

const dirtBikeRichContent = `
Proper preparation protects your bike from damage and ensures it's ready to ride when you are. Follow these essential steps before storing your dirt bike:

**Cleaning and Fuel System**
- Wash thoroughly to remove mud and debris
- Add fuel stabilizer to a full tank and run the engine for 5 minutes OR drain the fuel system completely for storage exceeding 6 months

**Fluids and Lubrication**
- Change the oil and filter
- Lubricate the chain, cables, and pivot points to prevent rust

**Battery and Tires**
- Remove the battery and store it on a trickle charger in a temperature-controlled location
- Inflate tires to the manufacturer's specifications

**Final Protection**
- Cover your dirt bike with a breathable cover
`;

const DirtBikeFAQs = [
	{
		question: "Can I store a dirt bike in a regular storage unit?",
		answer: `Yes, most storage facilities allow dirt bikes in standard units. Choose a ground-floor or drive-up unit for easiest access, and ensure the unit size accommodates your bike's length (most dirt bikes are 6.5-7.5 feet long). Always drain the fuel tank before storage per facility requirements.`
	},
	{
		question: "What's the best way to prepare my dirt bike for storage?",
		answer: `Clean your bike thoroughly, change the oil, stabilize the fuel or drain the tank completely, and disconnect the battery. Inflate tires to proper pressure, lubricate the chain, and cover the bike to prevent dust accumulation—these dirt bike storage ideas help maintain your bike's condition during extended storage.`
	},
	{
		question: "Do I need climate-controlled storage for my dirt bike?",
		answer: `Climate control isn't essential, but it helps prevent rust, corrosion, and degradation of rubber components in humid climates. If you're storing your dirt bike in garage-style indoor conditions for over six months, climate control provides added protection for your investment.`
	},
	{
		question: "What size storage unit fits a dirt bike and gear?",
		answer: `A 5x10 unit fits most dirt bikes with basic gear, while a 5x15 provides ample room for comprehensive gear storage, including helmets, boots, jerseys, and tools. If you're storing multiple bikes or a trailer, consider a 10x10 or larger.`
	},
	{
		question: "Can I work on my dirt bike at the storage facility?",
		answer: `Policies vary by facility. Some allow minor maintenance while others prohibit it due to safety and environmental regulations. Most facilities don't permit running engines indoors, but you can typically perform basic maintenance like chain cleaning or tire checks.`
	}
];

const dirtBikeHerodescription = `
Keep your dirt bike protected and your garage clutter-free with convenient dirt bike storage ideas from Storage.com. Whether you're between riding seasons, need space for gear storage, or want to keep your dirt bike in garage-quality conditions without sacrificing home or garage space, there's a storage unit for dirt bike enthusiasts.
`;

export const PSPDirtBikeContent = {
	type: "Dirt Bike",
	sections: [
		{
			title: "Dirt Bike hero",
			noPadding: true,
			modules: [
				{
					type: "hero-section",
					props: {
						title: "Find the Top Dirt Bike Storage",
						description: dirtBikeHerodescription,
						searchPlaceholder: "Search by city, state, or zip...",
						buttonText: "Find Dirt Bike Storage",
						heroImages: {
							desktop: HeaderBackgroundDesktop,
							tablet: HeaderBackgroundTablet,
							mobile: HeaderBackgroundMobile
						},
						heroAltText: "Dirt Bike Storage Hero Image"
					}
				}
			]
		},
		{
			title: "Secure Storage Units for Dirt Bike Riders",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "Secure Storage Units for Dirt Bike Riders",
						description:
							"Dirt bike storage is designed for riders who need a safe, accessible place to keep their bike and equipment between adventures. From weekend warriors to competitive racers, storage units offer protection from theft, weather damage, and unauthorized access that [garage storage can’t provide.](https://www.storage.com/blog/garage-vs-storage-unit-5-benefits-of-self-storage/)",
						content: [
							{
								id: "1",
								value: "**Indoor Dirt Bike Storage:** Climate-controlled units protect your bike from humidity, temperature swings, and rust. These facilities offer premium protection for high-end bikes and sensitive gear, making them ideal for long-term storage between seasons."
							},
							{
								id: "2",
								value: "**Drive-Up Storage Units:** Individual garage-style units provide [convenient ground-level access for easy loading and unloading](https://www.storage.com/blog/what-is-drive-up-storage/). Perfect for riders who need frequent access to their dirt bike in garage-style conditions, these units let you drive right up to your door and roll your bike in and out without hassle."
							},
							{
								id: "3",
								value: "**Outdoor Storage:** Budget-friendly covered or uncovered spaces work well for short-term needs. While your bike remains exposed to weather, [gated security and surveillance](https://www.storage.com/blog/self-storage-security-features/) provide theft deterrence at a lower price point."
							},
							{
								id: "4",
								value: "Dirt bike gear storage is also made possible with indoor and drive-up storage units—so you can keep your helmets, boots, and maintenance supplies close to your bike in storage."
							}
						],
						background: "light-blue",
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Size Guide Table/Storage Size Chart",
			noPadding: true,
			modules: [
				{
					type: "size-chart",
					props: {
						title: "What Storage Unit Size Do I Need for My Dirt Bike?",
						description:
							"Dirt bike in your garage, but ready for a new home in a storage unit? Our quick guide will help you find the correct unit size for your ride. Your bike is likely to find a home in one of these popular dirt bike storage sizes: [5x10](https://www.storage.com/blog/5x10-storage-unit-size-guide/), [5x15](https://www.storage.com/blog/5x15-storage-unit-size-guide/), [10x10](https://www.storage.com/blog/10x10-storage-unit-size-guide/), [10x15](https://www.storage.com/blog/10x15-storage-unit-size-guide/).",
						storages: [
							{
								title: "5x10",
								bestFor: [
									"Single dirt bike",
									"Helmet",
									"Minimal gear"
								]
							},
							{
								title: "5x15",
								bestFor: [
									"Bike plus gear storage",
									"Gear & tools",
									"Maintenance equipment"
								]
							},
							{
								title: "10x10",
								bestFor: [
									"Multiple bikes or bike with trailer",
									"Full gear kit",
									"Workbench"
								]
							},
							{
								title: "10x15",
								bestFor: [
									"Larger collections and gear",
									"Workshop space"
								]
							}
						]
					}
				}
			]
		},
		{
			title: "Dirt Bike Storage Steps",
			noPadding: true,
			modules: [
				{
					type: "size-comparison",
					props: {
						title: "How Much Does Dirt Bike Storage Cost?",
						description:
							"Renting a storage unit for your dirt bike is a great investment, and can even improve your dirt bike gear storage. Dirt bike storage costs vary by location, unit size, and amenities. We’ve taken a look at our network to find current pricing averages nationwide.",
						image: sizeComparison,
						sizes: [
							{
								id: "5x5",
								label: "5x5",
								title: "5' Wide x 5' Long",
								description:
									" - $35-45/month\n" +
									" - Fits a single bike with minimal gear"
							},
							{
								id: "5x10",
								label: "5x10",
								title: "5' Wide x 10' Long",
								description:
									" - $50-60/month\n" +
									" - Fits a bike with room for full gear storage"
							},
							{
								id: "10x10",
								label: "10x10",
								title: "10' Wide x 10' Long",
								description:
									" - $82-95/month\n" +
									" - Room for 1–2 bikes and ample additional storage space"
							},
							{
								id: "10x15",
								label: "10x15",
								title: "10' Wide x 15' Long",
								description:
									" - $110-120/month\n" +
									" - Fits 2–3 bikes with room for gear and a small workspace"
							}
						],
						postModuleDescription:
							"Prices fluctuate based on your region, with urban areas typically running higher than rural locations. Indoor and [climate-controlled units](https://www.storage.com/blog/climate-controlled-storage-units/) cost more but provide superior protection for expensive bikes and sensitive equipment. Many facilities offer discounts for long-term rentals, military members, or first-time customers—search Storage.com to compare current rates in your area."
					}
				}
			]
		},
		{
			title: "Rich Content: Dirt Bike Storage Steps",
			noPadding: true,
			modules: [
				{
					type: "video-content",
					props: {
						title: "Using a Storage Unit for a Dirtbike All Year Long",
						description: "",
						mediaUrl: DirtBikeImage,
						mediaType: "image",
						additionalContent: true,
						markDown: dirtBikeRichContent,
						videoTitle: "",
						textAlign: "left"
					}
				}
			]
		},
		{
			title: "Dirt Bike Inline CTA",
			noPadding: true,
			modules: [
				{
					type: "inline-cta",
					props: {
						storageType: "Dirt Bike",
						subheading: "Make a free reservation in minutes!",
						searchPlaceholder: "Search by city, state, or zip...",
						searchButtonText: "Find Storage",
						image: ctaMan,
						imageAltText: "inlineCTA image",
						background: "white"
					}
				}
			]
		},
		{
			title: "Reviews",
			noPadding: true,
			modules: [
				{
					type: "reviews",
					props: {
						title: "Storage.com has 4.4 out of 5 star rating on",
						testimonials: [
							{
								body: "It was easy to find a location I needed, and once I sent my request, the gal called me that evening and was super friendly. I've been stressed about finding a place to store my car between visits, and this experience made it so easy, and now I can relax!",
								author: "Marcia Pierce-rasmussen",
								title: "Super easy and relaxing experience!",
								imageSrc: AnnAlarcon
							},
							{
								body: "Very helpful. I needed a storage space for my car that had to be a drive-up and climate-controlled, and 10x20. Efficient searching and found exactly what I was looking for, and held it for me.",
								author: "Blaine Dyson",
								title: "Very helpful",
								imageSrc: GeorgeRansom
							},
							{
								body: "This was a great place to look for local storage for my vehicle, which was a sports car for the summer. They were able to find me the closest and cheapest storage, which is not easy. They also provide terms and costs, so it was no surprise when I signed up.",
								author: "David W. Young",
								title: "Excellent Place to Look for Vehicle Storage",
								imageSrc: SMurphy
							}
						]
					}
				}
			]
		},
		{
			title: "Dirt Bike Collapsible FAQs",
			noPadding: true,
			modules: [
				{
					type: "faq",
					props: {
						title: "Dirt Bike Storage FAQs",
						faqs: DirtBikeFAQs
					}
				}
			]
		},
		{
			title: "Related Content",
			noPadding: true,
			modules: [
				{
					type: "related-content",
					props: {
						heading: "Learn More About Dirt Bike Storage",
						unitCards: [
							{
								category: "Recreational Storage",
								content:
									"The Ultimate Dirt Bike Storage Unit Guide: 7 Things to Expect (and Get Right)",
								date: "July 16, 2025",
								image: HowToStore,
								url: "https://www.storage.com/blog/dirt-bike-storage-guide/"
							},
							{
								category: "Self Storage",
								content:
									"How Much Does Vehicle Storage Cost? Comprehensive Guide with Price Comparisons",
								date: "September 8, 2025",
								image: DriveUpStorage,
								url: "https://www.storage.com/blog/cost-of-car-storage/"
							},
							{
								category: "Recreational Storage",
								content:
									"What is a Battery Tender? A Complete Guide for Vehicle Storage",
								date: "August 4, 2025",
								image: Industry,
								url: "https://www.storage.com/blog/what-is-battery-tender-vehicle-storage/"
							}
						]
					}
				}
			]
		},
		{
			title: "Dirt Bike PSP Links",
			noPadding: true,
			modules: [
				{
					type: "psp-links",
					props: {
						background: "white",
						storageGridContent: {
							title: "More Ways to Store",
							image: illustrationSearch,
							storageTypes: storageCategories.dirtBike
						}
					}
				}
			]
		},
		{
			title: "Storage Near You",
			noPadding: true,
			modules: [
				{
					type: "nearby-cities",
					props: {
						pageType: "PSP",
						background: "light-blue"
					}
				}
			]
		}
	]
};

export default PSPDirtBikeContent;
